import { isDeviceAction } from '@cafe/shared';
import { BaseStrategy } from './base';
/**
 * State Machine strategy for complex flows with cycles, cross-links, or converging paths
 *
 * Implements the "Virtual CPU" pattern:
 * - current_node: A variable acting as the Program Counter
 * - repeat: A loop that keeps the automation alive until END
 * - choose: A dispatcher that executes the current node's logic
 *
 * This allows for arbitrary graph topologies including:
 * - Back-loops (returning to earlier nodes)
 * - Cross-links (jumping across branches)
 * - Converging paths (multiple paths merging)
 * - Complex state machines
 */
export class StateMachineStrategy extends BaseStrategy {
    name = 'state-machine';
    description = 'Generates state machine YAML for complex flows with cycles or cross-links';
    canHandle(_analysis) {
        // State machine can handle any topology
        return true;
    }
    generate(flow, analysis) {
        const warnings = [];
        // Build trigger-to-action mapping for routing
        const triggerRouting = this.buildTriggerRouting(flow);
        if (triggerRouting.size === 0) {
            warnings.push('No action nodes found after triggers');
            // Extract triggers to determine output format
            const triggers = this.extractTriggers(flow);
            if (triggers.length > 0) {
                // Output as automation with empty action
                return {
                    automation: {
                        alias: flow.name,
                        description: flow.description || '',
                        triggers: triggers,
                        actions: [],
                        mode: flow.metadata?.mode ?? 'single',
                    },
                    warnings,
                    strategy: this.name,
                };
            }
            // No triggers - output as script
            return {
                script: {
                    alias: flow.name,
                    description: flow.description || '',
                    sequence: [],
                    mode: flow.metadata?.mode ?? 'single',
                },
                warnings,
                strategy: this.name,
            };
        }
        // Plan trigger fan-out with the same rules as node fan-out, so a target that
        // another trigger also routes to keeps its dispatcher entry instead of
        // silently becoming unreachable.
        const triggerNodes = flow.nodes.filter((n) => n.type === 'trigger');
        const parallelConsumedNodeIds = new Set();
        const approvedTriggerFanOut = new Map();
        for (const [idx, routedTargets] of triggerRouting) {
            const triggerId = triggerNodes[idx]?.id;
            if (!triggerId)
                continue;
            // Duplicate edges to one target are a single branch, not a fan-out
            const targets = [...new Set(routedTargets)].filter((t) => t !== 'END');
            if (targets.length <= 1)
                continue;
            const plan = this.planFanOut(flow, this.getOutgoingEdges(flow, triggerId));
            if (!plan.ok) {
                warnings.push(this.describeFanOutRejection(`Trigger ${idx + 1}`, targets, plan));
                continue;
            }
            approvedTriggerFanOut.set(idx, targets);
            for (const id of plan.owned) {
                parallelConsumedNodeIds.add(id);
            }
        }
        const parallelEntryBlocks = this.generateParallelEntryBlocks(flow, approvedTriggerFanOut);
        // Nodes that fan out to several successors inline those successors into a
        // parallel block, so their subgraphs lose their standalone entries — but only
        // the parts nothing outside the branch set can reach (see planFanOut).
        // Walk outward from the entry points so an outer fan-out always claims its
        // subgraph before any nested one does. Iterating flow.nodes in array order
        // would let a nested node claim first, which strands the outer node's other
        // branches and drops their edges.
        const fanOutPlans = new Map();
        for (const node of this.orderNodesFromEntry(flow)) {
            // Conditions branch through their own true/false path and never build a
            // fan-out tail, so their targets must keep their standalone entries.
            if (node.type === 'trigger' || node.type === 'condition')
                continue;
            // Already inlined into an ancestor's parallel branch. Its own fan-out is
            // emitted by continueInlineBranch, so there is nothing to plan or warn about.
            if (parallelConsumedNodeIds.has(node.id))
                continue;
            const targets = this.getFanOutTargets(flow, node.id);
            if (targets.length <= 1)
                continue;
            const plan = this.planFanOut(flow, this.getFanOutEdges(flow, node.id));
            if (!plan.ok) {
                warnings.push(this.describeFanOutRejection(`Node "${node.id}"`, targets, plan));
                continue;
            }
            fanOutPlans.set(node.id, targets);
            for (const id of plan.owned) {
                parallelConsumedNodeIds.add(id);
            }
            // Nested fan-outs are inlined by continueInlineBranch regardless of their
            // own plan, so report their rejections here — across everything inlined,
            // not just what this fan-out owns.
            for (const branchNodeId of plan.reachable) {
                const branchTargets = this.getFanOutTargets(flow, branchNodeId);
                if (branchTargets.length <= 1)
                    continue;
                const branchPlan = this.planFanOut(flow, this.getFanOutEdges(flow, branchNodeId));
                if (!branchPlan.ok) {
                    warnings.push(this.describeFanOutRejection(`Node "${branchNodeId}"`, branchTargets, branchPlan, true));
                }
            }
        }
        // A condition handle may also lead to several nodes. Route those through a
        // synthetic parallel entry rather than keeping only the first edge.
        const conditionHandleEntries = [];
        const conditionHandleTargets = new Map();
        for (const node of this.orderNodesFromEntry(flow)) {
            if (node.type !== 'condition' || parallelConsumedNodeIds.has(node.id))
                continue;
            for (const handle of ['true', 'false']) {
                const targets = this.getConditionHandleTargets(flow, node.id, handle);
                if (targets.length <= 1)
                    continue;
                const handleEdges = this.getOutgoingEdges(flow, node.id).filter((e) => e.sourceHandle === handle);
                const plan = this.planFanOut(flow, handleEdges);
                if (!plan.ok) {
                    warnings.push(this.describeFanOutRejection(`The "${handle}" branch of condition "${node.id}"`, targets, plan));
                    continue;
                }
                const entryId = `__parallel_cond_${node.id}__${handle}`;
                conditionHandleTargets.set(`${node.id}:${handle}`, entryId);
                conditionHandleEntries.push(this.buildParallelEntryBlock(flow, entryId, targets));
                for (const id of plan.owned) {
                    parallelConsumedNodeIds.add(id);
                }
            }
        }
        // Build choose blocks for each non-trigger node not already inlined in a parallel branch
        const nodeBlocks = flow.nodes
            .filter((n) => n.type !== 'trigger' && !parallelConsumedNodeIds.has(n.id))
            .map((node) => this.generateNodeBlock(flow, node, fanOutPlans, conditionHandleTargets));
        // Combine parallel entry blocks and remaining node blocks
        const chooseBlocks = [...parallelEntryBlocks, ...conditionHandleEntries, ...nodeBlocks];
        // Warn about potential infinite loops
        if (analysis.hasCycles) {
            const cycleWarning = this.detectPotentialInfiniteLoop(flow, analysis);
            if (cycleWarning) {
                warnings.push(cycleWarning);
            }
        }
        // Extract triggers for the automation wrapper
        const triggers = this.extractTriggers(flow);
        // Generate the initial node expression
        // If all triggers lead to the same node, use that directly
        // Otherwise, use a Jinja2 template to route based on trigger.idx
        const entryNodeExpr = this.generateEntryNodeExpression(triggerRouting, approvedTriggerFanOut);
        // Build the action sequence for the state machine
        // In HA automations, actions are a flat list - we use:
        // 1. A variables action to initialize state
        // 2. A repeat action with choose dispatcher
        const actionSequence = [
            // Initialize the state machine variables
            {
                variables: {
                    current_node: entryNodeExpr,
                    flow_context: {},
                },
            },
            // The main execution loop
            {
                alias: 'State Machine Loop',
                repeat: {
                    until: '{{ current_node == "END" }}',
                    sequence: [
                        {
                            choose: chooseBlocks,
                            default: [
                                {
                                    service: 'system_log.write',
                                    data: {
                                        message: 'C.A.F.E.: Unknown state "{{ current_node }}", ending flow',
                                        level: 'warning',
                                    },
                                },
                                {
                                    variables: {
                                        current_node: 'END',
                                    },
                                },
                            ],
                        },
                    ],
                },
            },
        ];
        // If there are triggers, output as automation format
        if (triggers.length > 0) {
            return {
                automation: {
                    alias: flow.name,
                    description: flow.description || '',
                    triggers: triggers,
                    actions: actionSequence,
                    mode: flow.metadata?.mode ?? 'single',
                },
                warnings,
                strategy: this.name,
            };
        }
        // No triggers - output as script format
        return {
            script: {
                alias: flow.name,
                description: flow.description || '',
                sequence: actionSequence,
                mode: flow.metadata?.mode ?? 'single',
            },
            warnings,
            strategy: this.name,
        };
    }
    /**
     * Build a mapping from trigger index to target action node(s)
     * Returns a Map where key = trigger index, value = array of target node IDs
     * When a trigger has multiple targets, they should execute in parallel
     */
    buildTriggerRouting(flow) {
        const routing = new Map();
        // Get trigger nodes in order (they will be output in this order)
        const triggerNodes = flow.nodes.filter((n) => n.type === 'trigger');
        triggerNodes.forEach((trigger, index) => {
            const outgoing = this.getOutgoingEdges(flow, trigger.id);
            if (outgoing.length > 0) {
                routing.set(index, outgoing.map((e) => e.target));
            }
        });
        return routing;
    }
    /**
     * Get the effective entry point for a trigger
     * If trigger has single target, return that target ID
     * If trigger has multiple targets (parallel), return synthetic parallel entry ID
     */
    getEffectiveEntryPoint(triggerIndex, targets, approvedTriggerFanOut) {
        // Only triggers whose fan-out was approved get a synthetic parallel entry;
        // the rest fall back to their first target.
        if (targets.length === 1 || !approvedTriggerFanOut.has(triggerIndex)) {
            return targets[0];
        }
        return `__parallel_trigger_${triggerIndex}`;
    }
    /**
     * Generate the entry node expression for initialization
     * If all triggers lead to the same node, return that node ID
     * Otherwise, return a Jinja2 template that routes based on trigger.idx
     */
    generateEntryNodeExpression(triggerRouting, approvedTriggerFanOut) {
        // Convert to effective entry points (handling parallel branches)
        const effectiveEntries = new Map();
        for (const [idx, targets] of triggerRouting) {
            effectiveEntries.set(idx, this.getEffectiveEntryPoint(idx, targets, approvedTriggerFanOut));
        }
        const uniqueTargets = new Set(effectiveEntries.values());
        // If all triggers lead to the same node (or there's only one trigger)
        if (uniqueTargets.size === 1) {
            return [...uniqueTargets][0];
        }
        // Multiple different targets - generate routing template
        // Using trigger.idx which is 0-based index of which trigger fired
        const entries = [...effectiveEntries.entries()].sort((a, b) => a[0] - b[0]);
        // Build a Jinja2 if/elif chain
        // Note: trigger.idx is a string in HA, so compare with quoted string values
        // Node IDs should NOT be quoted - they're compared with quoted strings in conditions
        const parts = [];
        entries.forEach(([idx, nodeId], i) => {
            if (i === 0) {
                parts.push(`{% if trigger.idx == "${idx}" %}${nodeId}`);
            }
            else if (i === entries.length - 1) {
                parts.push(`{% else %}${nodeId}{% endif %}`);
            }
            else {
                parts.push(`{% elif trigger.idx == "${idx}" %}${nodeId}`);
            }
        });
        // Handle edge case where we have only one entry
        if (entries.length === 1) {
            return entries[0][1];
        }
        return parts.join('');
    }
    /**
     * Generate choose blocks for parallel entry points
     * When a trigger has multiple targets, we create a synthetic state that
     * executes all targets in a parallel block
     */
    generateParallelEntryBlocks(flow, triggerRouting) {
        const parallelBlocks = [];
        for (const [idx, targets] of triggerRouting) {
            // Only generate parallel blocks for triggers with multiple targets
            if (targets.length <= 1) {
                continue;
            }
            parallelBlocks.push(this.buildParallelEntryBlock(flow, `__parallel_trigger_${idx}`, [...new Set(targets)]));
        }
        return parallelBlocks;
    }
    /**
     * Build a synthetic dispatcher entry that runs several branches in parallel
     * and then ends the flow. Used for trigger fan-out and for condition handles
     * that lead to more than one node.
     */
    buildParallelEntryBlock(flow, entryId, targets) {
        return {
            conditions: [
                {
                    condition: 'template',
                    value_template: `{{ current_node == "${entryId}" }}`,
                },
            ],
            sequence: [
                { parallel: this.buildParallelBranches(flow, targets) },
                { variables: { current_node: 'END' } },
            ],
        };
    }
    /**
     * Outgoing edges that represent parallel fan-out rather than conditional
     * branching. Edges carrying a true/false handle belong to a condition node
     * and are excluded.
     */
    getFanOutEdges(flow, nodeId) {
        return this.getOutgoingEdges(flow, nodeId).filter((e) => e.sourceHandle !== 'true' && e.sourceHandle !== 'false');
    }
    /**
     * Nodes ordered by distance from the trigger entry points (breadth-first),
     * with any unreachable nodes appended in declaration order.
     *
     * Fan-out planning must be deterministic and outermost-first: whichever node
     * claims a subgraph first owns it, so processing a nested node before its
     * ancestor would strand the ancestor's remaining branches.
     */
    orderNodesFromEntry(flow) {
        const byId = new Map(flow.nodes.map((n) => [n.id, n]));
        const ordered = [];
        const seen = new Set();
        const queue = flow.nodes
            .filter((n) => n.type === 'trigger')
            .flatMap((trigger) => this.getOutgoingEdges(flow, trigger.id).map((e) => e.target));
        while (queue.length > 0) {
            const nodeId = queue.shift();
            if (nodeId === undefined || nodeId === 'END' || seen.has(nodeId))
                continue;
            seen.add(nodeId);
            const node = byId.get(nodeId);
            if (!node)
                continue;
            ordered.push(node);
            for (const edge of this.getOutgoingEdges(flow, nodeId)) {
                queue.push(edge.target);
            }
        }
        // Nodes not reachable from any trigger still need blocks generated
        for (const node of flow.nodes) {
            if (node.type !== 'trigger' && !seen.has(node.id)) {
                ordered.push(node);
            }
        }
        return ordered;
    }
    /**
     * Distinct targets on one handle of a condition node.
     */
    getConditionHandleTargets(flow, nodeId, handle) {
        const targets = this.getOutgoingEdges(flow, nodeId)
            .filter((e) => e.sourceHandle === handle)
            .map((e) => e.target);
        return [...new Set(targets)].filter((t) => t !== 'END');
    }
    /**
     * Distinct fan-out targets for a node. Duplicate edges to the same target
     * would otherwise produce duplicate branches that run the target twice.
     */
    getFanOutTargets(flow, nodeId) {
        return [...new Set(this.getFanOutEdges(flow, nodeId).map((e) => e.target))].filter((target) => target !== 'END' && target !== nodeId);
    }
    /**
     * Plan a fan-out: which branch nodes may lose their standalone dispatcher entry.
     *
     * Branches are always inlined so every one of them runs. Inlining additionally
     * *removes* a node's dispatcher entry, which is only safe when nothing outside
     * the branch set jumps to it — a node reachable from another trigger or an
     * unrelated predecessor keeps its entry and is simply emitted in both places.
     * The two executions belong to different runs, so that is correct, not duplication.
     *
     * Returns the set of nodes safe to consume, or null when the fan-out cannot be
     * inlined at all because a branch loops back to the source — an inline branch
     * has no way to re-enter the dispatcher.
     */
    planFanOut(flow, fanOutEdges) {
        const sourceId = fanOutEdges[0]?.source;
        if (!sourceId)
            return { ok: false, reason: 'cycle', nodes: [] };
        const targets = [...new Set(fanOutEdges.map((e) => e.target))];
        const reachable = new Set();
        for (const target of targets) {
            this.collectSubgraphNodeIds(flow, target, reachable);
        }
        // A branch that loops back to the source cannot be expressed inline
        if (reachable.has(sourceId)) {
            return { ok: false, reason: 'cycle', nodes: [sourceId] };
        }
        // Branches are inlined independently, so anything two of them share would be
        // emitted — and executed — once per branch. Leave those flows sequential
        // rather than silently doubling the work.
        const joined = this.findJoinNodes(flow, targets);
        if (joined.length > 0) {
            return { ok: false, reason: 'join', nodes: joined };
        }
        // generateInlineBranch resolves a condition's handles with a single lookup,
        // so a condition whose handle has several targets cannot be represented
        // inline without losing edges. Keep those on the dispatcher path instead.
        const lossyCondition = [...reachable].find((id) => this.hasMultiTargetHandle(flow, id));
        if (lossyCondition) {
            return { ok: false, reason: 'nested-condition', nodes: [lossyCondition] };
        }
        // A node's entry may only be dropped when *every* path into it is inlined.
        // Checking against `reachable` alone is not enough: a branch root with an
        // outside predecessor keeps its entry, and everything downstream of it is
        // then only reachable through a node that still dispatches. Shrink to a
        // fixpoint so no surviving entry ever transitions to a deleted one.
        const fanOutEdgeIds = new Set(fanOutEdges.map((e) => e.id));
        const owned = new Set(reachable);
        let changed = true;
        while (changed) {
            changed = false;
            for (const id of [...owned]) {
                const hasOutsideEntry = flow.edges.some((edge) => edge.target === id && !fanOutEdgeIds.has(edge.id) && !owned.has(edge.source));
                if (hasOutsideEntry) {
                    owned.delete(id);
                    changed = true;
                }
            }
        }
        return { ok: true, owned, reachable };
    }
    /**
     * True when the node is a condition with more than one target on a handle.
     */
    hasMultiTargetHandle(flow, nodeId) {
        const node = flow.nodes.find((n) => n.id === nodeId);
        if (node?.type !== 'condition')
            return false;
        return (this.getConditionHandleTargets(flow, nodeId, 'true').length > 1 ||
            this.getConditionHandleTargets(flow, nodeId, 'false').length > 1);
    }
    /**
     * Explain, in the user's terms, why a set of branches could not be run in
     * parallel and what will happen instead.
     */
    describeFanOutRejection(subject, targets, plan, 
    /** Nested fan-outs are inlined regardless, so the consequence differs. */
    inlined = false) {
        if (plan.reason === 'nested-condition') {
            return `${subject} branches to [${targets.join(', ')}], but condition "${plan.nodes.join(', ')}" downstream sends one of its branches to several nodes, which cannot be nested inside a parallel block. Only the first branch will run. Move that condition out of the parallel section to run these in parallel.`;
        }
        if (plan.reason === 'cycle') {
            return inlined
                ? `${subject} branches to [${targets.join(', ')}] inside a parallel section, but one of those loops back. The loop cannot be expressed there and will stop after one pass.`
                : `${subject} branches to [${targets.join(', ')}], but one of those leads back into the same path. A parallel branch cannot loop back, so only the first branch will run.`;
        }
        if (inlined) {
            return `${subject} branches to [${targets.join(', ')}] inside a parallel section, and those branches re-join at [${plan.nodes.join(', ')}]. Nested parallel branches cannot re-join, so [${plan.nodes.join(', ')}] will run once per branch.`;
        }
        return `${subject} branches to [${targets.join(', ')}], but those branches re-join at [${plan.nodes.join(', ')}]. Parallel branches cannot re-join, so only the first branch will run. Give each branch its own downstream nodes to run them in parallel.`;
    }
    /**
     * Nodes reachable from more than one of the given branches. Parallel branches
     * are inlined independently, so anything they share would run once per branch.
     */
    findJoinNodes(flow, targets) {
        const seen = new Set();
        const duplicated = new Set();
        for (const target of targets) {
            const branchNodes = new Set();
            this.collectSubgraphNodeIds(flow, target, branchNodes);
            for (const id of branchNodes) {
                if (seen.has(id)) {
                    duplicated.add(id);
                }
                seen.add(id);
            }
        }
        return [...duplicated];
    }
    /**
     * Build self-contained inline branches for a set of target nodes.
     * Each branch gets a "parallel_branch:<nodeId>" alias so the parser can
     * identify which node each branch corresponds to on the way back in.
     */
    buildParallelBranches(flow, targets, visited = new Set()) {
        return targets.map((targetId) => {
            // Each branch gets its own visited copy so sibling branches don't block each other
            const inlineActions = this.generateInlineBranch(flow, targetId, new Set(visited));
            if (inlineActions.length === 0) {
                return { alias: `parallel_branch:${targetId}`, stop: 'Empty branch' };
            }
            // Always wrap in a sequence. Spreading a single action and overwriting its
            // alias would destroy the "cafe_node:<id>:<userAlias>" encoding and lose
            // the node's user-facing alias on the way back in.
            return { alias: `parallel_branch:${targetId}`, sequence: inlineActions };
        });
    }
    /**
     * Build the tail of a node's state-machine sequence.
     * A single outgoing edge advances current_node to the next node; multiple
     * outgoing edges fan out into a parallel block of self-contained branches
     * and then end the flow, since all downstream work happens inside them.
     */
    buildTransitionTail(flow, nodeId, edges, fanOutPlans) {
        // Only fan out when the branches were verified as exclusively owned by this
        // node; otherwise fall through to the sequential transition below.
        const approvedTargets = fanOutPlans.get(nodeId);
        if (approvedTargets && approvedTargets.length > 1) {
            return [
                { parallel: this.buildParallelBranches(flow, approvedTargets) },
                { variables: { current_node: 'END' } },
            ];
        }
        return [{ variables: { current_node: edges[0]?.target ?? 'END' } }];
    }
    /**
     * Collect all node IDs reachable from a starting node (used to identify
     * nodes that are already inlined inside parallel branches).
     */
    collectSubgraphNodeIds(flow, nodeId, collected) {
        if (nodeId === 'END' || collected.has(nodeId))
            return;
        const node = flow.nodes.find((n) => n.id === nodeId);
        if (!node || node.type === 'trigger')
            return;
        collected.add(nodeId);
        const edges = this.getOutgoingEdges(flow, node.id);
        for (const edge of edges) {
            this.collectSubgraphNodeIds(flow, edge.target, collected);
        }
    }
    /**
     * Encode a C.A.F.E. node ID into the alias field so it survives the HA round-trip.
     * Format: "cafe_node:<nodeId>" or "cafe_node:<nodeId>:<userAlias>"
     */
    encodeNodeIdInAlias(action, nodeId) {
        const existingAlias = action.alias;
        action.alias = existingAlias ? `cafe_node:${nodeId}:${existingAlias}` : `cafe_node:${nodeId}`;
    }
    /**
     * Generate an inline HA action sequence for a subgraph starting at the given node.
     * Used within parallel branches where each branch must be self-contained
     * (no current_node state machine variable).
     */
    /**
     * Continue an inline branch past the current node. A single successor is
     * appended sequentially; several successors fan out into a nested parallel
     * block so none of them are dropped.
     */
    continueInlineBranch(flow, nodeId, edges, visited) {
        // Same de-duping as the top-level path, so duplicate edges to one target
        // don't produce duplicate branches that run it twice.
        const targets = this.getFanOutTargets(flow, nodeId);
        if (targets.length > 1) {
            return [{ parallel: this.buildParallelBranches(flow, targets, visited) }];
        }
        return this.generateInlineBranch(flow, targets[0] ?? edges[0]?.target ?? 'END', visited);
    }
    generateInlineBranch(flow, nodeId, visited) {
        if (nodeId === 'END' || visited.has(nodeId)) {
            return [];
        }
        const node = flow.nodes.find((n) => n.id === nodeId);
        if (!node)
            return [];
        visited.add(nodeId);
        const edges = this.getOutgoingEdges(flow, node.id);
        switch (node.type) {
            case 'action': {
                const actionCall = this.buildActionCall(node);
                this.encodeNodeIdInAlias(actionCall, node.id);
                return [actionCall, ...this.continueInlineBranch(flow, node.id, edges, visited)];
            }
            case 'condition': {
                const trueEdge = edges.find((e) => e.sourceHandle === 'true');
                const falseEdge = edges.find((e) => e.sourceHandle === 'false');
                const trueTarget = trueEdge?.target ?? 'END';
                const falseTarget = falseEdge?.target ?? 'END';
                const condition = this.buildNativeCondition(node);
                // Each branch gets its own visited copy so independent paths don't block each other
                const thenActions = this.generateInlineBranch(flow, trueTarget, new Set(visited));
                const elseActions = this.generateInlineBranch(flow, falseTarget, new Set(visited));
                const ifBlock = {
                    alias: node.data.alias,
                    if: [condition],
                    then: thenActions,
                };
                this.encodeNodeIdInAlias(ifBlock, node.id);
                if (elseActions.length > 0) {
                    ifBlock.else = elseActions;
                }
                return [ifBlock];
            }
            case 'delay': {
                const delayAction = this.buildDelayAction(node);
                this.encodeNodeIdInAlias(delayAction, node.id);
                return [delayAction, ...this.continueInlineBranch(flow, node.id, edges, visited)];
            }
            case 'wait': {
                const waitAction = this.buildWaitAction(node);
                this.encodeNodeIdInAlias(waitAction, node.id);
                return [waitAction, ...this.continueInlineBranch(flow, node.id, edges, visited)];
            }
            case 'set_variables': {
                const setVarsAction = this.buildSetVariablesAction(node);
                this.encodeNodeIdInAlias(setVarsAction, node.id);
                return [setVarsAction, ...this.continueInlineBranch(flow, node.id, edges, visited)];
            }
            default: {
                // Unknown node type — skip it and continue to the next node
                return this.continueInlineBranch(flow, node.id, edges, visited);
            }
        }
    }
    /**
     * Extract triggers from trigger nodes
     */
    extractTriggers(flow) {
        return flow.nodes
            .filter((n) => n.type === 'trigger')
            .map((node) => this.buildTrigger(node));
    }
    /**
     * Generate a choose block for a single node
     */
    generateNodeBlock(flow, node, fanOutPlans, conditionHandleTargets) {
        const outgoingEdges = this.getOutgoingEdges(flow, node.id);
        switch (node.type) {
            case 'condition':
                return this.generateConditionBlock(node, outgoingEdges, conditionHandleTargets);
            case 'action':
                return this.generateActionBlock(flow, node, outgoingEdges, fanOutPlans);
            case 'delay':
                return this.generateDelayBlock(flow, node, outgoingEdges, fanOutPlans);
            case 'wait':
                return this.generateWaitBlock(flow, node, outgoingEdges, fanOutPlans);
            case 'set_variables':
                return this.generateSetVariablesBlock(flow, node, outgoingEdges, fanOutPlans);
            default:
                return this.generatePassthroughBlock(flow, node, outgoingEdges, fanOutPlans);
        }
    }
    /**
     * Generate block for action node
     * Executes the service call then moves to the next node
     */
    generateActionBlock(flow, node, edges, fanOutPlans) {
        const currentNodeId = node.id;
        const actionCall = this.buildActionCall(node);
        return {
            conditions: [
                {
                    condition: 'template',
                    value_template: `{{ current_node == "${currentNodeId}" }}`,
                },
            ],
            sequence: [actionCall, ...this.buildTransitionTail(flow, node.id, edges, fanOutPlans)],
        };
    }
    /**
     * Build service call action or device action
     */
    buildActionCall(node) {
        // Check if this is a device action (needs special format)
        if (isDeviceAction(node.data.data)) {
            const deviceData = node.data.data;
            const action = {
                device_id: deviceData.device_id,
                domain: deviceData.domain,
                type: deviceData.type,
            };
            if (node.data.alias) {
                action.alias = node.data.alias;
            }
            // Add entity_id if present
            if (deviceData.entity_id) {
                action.entity_id = deviceData.entity_id;
            }
            // Add subtype if present
            if (deviceData.subtype) {
                action.subtype = deviceData.subtype;
            }
            // Add any additional parameters (like 'option' for select)
            const knownFields = ['type', 'device_id', 'domain', 'entity_id', 'subtype'];
            for (const [key, value] of Object.entries(deviceData)) {
                if (!knownFields.includes(key) && value !== undefined) {
                    action[key] = value;
                }
            }
            if (node.data.enabled === false) {
                action.enabled = false;
            }
            return action;
        }
        // Check if this is a fallback repeat action (opaque repeat block)
        if (node.data.repeat) {
            const repeatData = node.data.repeat;
            const actionCall = {
                repeat: {
                    ...(repeatData.count !== undefined ? { count: repeatData.count } : {}),
                    ...(repeatData.while ? { while: repeatData.while } : {}),
                    ...(repeatData.until ? { until: repeatData.until } : {}),
                    sequence: repeatData.sequence ?? [],
                },
            };
            if (node.data.alias)
                actionCall.alias = node.data.alias;
            if (node.data.enabled === false)
                actionCall.enabled = false;
            return actionCall;
        }
        // Check if this is a fire event action
        if (typeof node.data.event === 'string' && node.data.event.trim() !== '') {
            const actionCall = { event: node.data.event };
            if (node.data.alias)
                actionCall.alias = node.data.alias;
            if (node.data.event_data && Object.keys(node.data.event_data).length > 0) {
                actionCall.event_data = node.data.event_data;
            }
            if (node.data.enabled === false)
                actionCall.enabled = false;
            return actionCall;
        }
        // Standard service call format
        // Use spread pattern to preserve unknown properties from custom integrations
        const { alias, service, id, target, data, data_template, response_variable, continue_on_error, enabled, repeat: _repeat, ...extraProps } = node.data;
        const actionCall = {
            ...extraProps, // Preserve extra properties
            alias,
            service,
        };
        if (id) {
            actionCall.id = id;
        }
        if (target) {
            actionCall.target = target;
        }
        if (data) {
            actionCall.data = data;
        }
        if (data_template) {
            actionCall.data_template = data_template;
        }
        if (response_variable) {
            actionCall.response_variable = response_variable;
        }
        if (continue_on_error) {
            actionCall.continue_on_error = continue_on_error;
        }
        if (enabled === false) {
            actionCall.enabled = false;
        }
        return actionCall;
    }
    /**
     * Generate block for condition node
     * Evaluates the condition and sets current_node based on result
     */
    generateConditionBlock(node, edges, conditionHandleTargets) {
        const trueEdge = edges.find((e) => e.sourceHandle === 'true');
        const falseEdge = edges.find((e) => e.sourceHandle === 'false');
        // A handle leading to several nodes routes through a synthetic parallel entry
        const trueTargetId = conditionHandleTargets.get(`${node.id}:true`) ?? trueEdge?.target ?? 'END';
        const falseTargetId = conditionHandleTargets.get(`${node.id}:false`) ?? falseEdge?.target ?? 'END';
        const trueTarget = trueTargetId === 'END' ? 'END' : trueTargetId;
        const falseTarget = falseTargetId === 'END' ? 'END' : falseTargetId;
        const currentNodeId = node.id;
        // Check if this is a complex template that can't be inlined into {% if %}
        const needsNativeCondition = this.needsNativeConditionCheck(node);
        if (needsNativeCondition) {
            // Use native HA condition check instead of Jinja2 {% if %}
            // This handles templates with {% set %} and other complex Jinja2
            const condition = this.buildNativeCondition(node);
            return {
                conditions: [
                    {
                        condition: 'template',
                        value_template: `{{ current_node == "${currentNodeId}" }}`,
                    },
                ],
                sequence: [
                    {
                        alias: node.data.alias,
                        if: [condition],
                        then: [
                            {
                                variables: {
                                    current_node: trueTarget,
                                },
                            },
                        ],
                        else: [
                            {
                                variables: {
                                    current_node: falseTarget,
                                },
                            },
                        ],
                    },
                ],
            };
        }
        // Generate Jinja2 template for condition evaluation (simple case)
        const conditionTemplate = this.buildConditionTemplate(node);
        return {
            conditions: [
                {
                    condition: 'template',
                    value_template: `{{ current_node == "${currentNodeId}" }}`,
                },
            ],
            sequence: [
                {
                    alias: node.data.alias,
                    variables: {
                        current_node: `{% if ${conditionTemplate} %}${trueTarget}{% else %}${falseTarget}{% endif %}`,
                    },
                },
            ],
        };
    }
    /**
     * Check if a condition node needs native HA condition check instead of Jinja2 {% if %}
     */
    needsNativeConditionCheck(node) {
        const data = node.data;
        // Template conditions with {% %} statements need native check
        if (data.condition === 'template') {
            const template = data.value_template || '';
            if (template.includes('{%')) {
                return true;
            }
        }
        // Nested conditions (and/or/not) with complex templates
        if ((data.condition === 'and' || data.condition === 'or' || data.condition === 'not') &&
            data.conditions) {
            return data.conditions.some((c) => {
                if (c.condition === 'template') {
                    const template = c.value_template || '';
                    return template.includes('{%');
                }
                return false;
            });
        }
        return false;
    }
    /**
     * Build native HA condition object for use in if/then/else
     */
    buildNativeCondition(node) {
        const data = node.data;
        const condition = {
            condition: data.condition,
        };
        // Copy relevant fields based on condition type
        if (data.entity_id)
            condition.entity_id = data.entity_id;
        if (data.state !== undefined)
            condition.state = data.state;
        if (data.above != null && data.above !== '')
            condition.above = data.above;
        if (data.below != null && data.below !== '')
            condition.below = data.below;
        if (data.attribute)
            condition.attribute = data.attribute;
        if (data.value_template)
            condition.value_template = data.value_template;
        if (data.after)
            condition.after = data.after;
        if (data.before)
            condition.before = data.before;
        if (data.after_offset)
            condition.after_offset = data.after_offset;
        if (data.before_offset)
            condition.before_offset = data.before_offset;
        if (data.zone)
            condition.zone = data.zone;
        if (data.weekday)
            condition.weekday = data.weekday;
        if (data.id)
            condition.id = data.id;
        // Handle nested conditions
        if (data.conditions && data.conditions.length > 0) {
            condition.conditions = data.conditions.map((c) => {
                const nested = {
                    condition: c.condition,
                };
                if (c.entity_id)
                    nested.entity_id = c.entity_id;
                if (c.state !== undefined)
                    nested.state = c.state;
                if (c.above !== undefined)
                    nested.above = c.above;
                if (c.below !== undefined)
                    nested.below = c.below;
                if (c.attribute)
                    nested.attribute = c.attribute;
                if (c.value_template)
                    nested.value_template = c.value_template;
                if (c.template)
                    nested.value_template = c.template;
                if (c.after)
                    nested.after = c.after;
                if (c.before)
                    nested.before = c.before;
                if (c.after_offset)
                    nested.after_offset = c.after_offset;
                if (c.before_offset)
                    nested.before_offset = c.before_offset;
                if (c.zone)
                    nested.zone = c.zone;
                if (c.weekday)
                    nested.weekday = c.weekday;
                if (c.id)
                    nested.id = c.id;
                return Object.fromEntries(Object.entries(nested).filter(([, v]) => v !== undefined));
            });
        }
        return Object.fromEntries(Object.entries(condition).filter(([, v]) => v !== undefined));
    }
    /**
     * Build a delay action from a delay node (without state-machine wrapper)
     */
    buildDelayAction(node) {
        // Use spread pattern to preserve unknown properties from custom integrations
        const { alias, delay, id, ...extraProps } = node.data;
        const delayAction = {
            ...extraProps, // Preserve extra properties
            alias,
            delay,
        };
        if (id) {
            delayAction.id = id;
        }
        return delayAction;
    }
    /**
     * Generate block for delay node
     */
    generateDelayBlock(flow, node, edges, fanOutPlans) {
        const currentNodeId = node.id;
        return {
            conditions: [
                {
                    condition: 'template',
                    value_template: `{{ current_node == "${currentNodeId}" }}`,
                },
            ],
            sequence: [
                this.buildDelayAction(node),
                ...this.buildTransitionTail(flow, node.id, edges, fanOutPlans),
            ],
        };
    }
    /**
     * Build a wait action from a wait node (without state-machine wrapper)
     */
    buildWaitAction(node) {
        // Use spread pattern to preserve unknown properties from custom integrations
        const { alias, id, wait_template, wait_for_trigger, timeout, continue_on_timeout, ...extraProps } = node.data;
        const waitAction = {
            ...extraProps, // Preserve extra properties
            alias,
        };
        if (id) {
            waitAction.id = id;
        }
        if (wait_template) {
            waitAction.wait_template = wait_template;
        }
        else if (wait_for_trigger) {
            waitAction.wait_for_trigger = wait_for_trigger.map((triggerData) => {
                const { alias: _alias, ...rest } = triggerData;
                const trigger = { ...rest };
                return Object.fromEntries(Object.entries(trigger).filter(([, v]) => v !== undefined && v !== '' && v !== null));
            });
        }
        if (timeout) {
            waitAction.timeout = timeout;
        }
        if (continue_on_timeout !== undefined) {
            waitAction.continue_on_timeout = continue_on_timeout;
        }
        return waitAction;
    }
    /**
     * Generate block for wait node
     */
    generateWaitBlock(flow, node, edges, fanOutPlans) {
        const currentNodeId = node.id;
        return {
            conditions: [
                {
                    condition: 'template',
                    value_template: `{{ current_node == "${currentNodeId}" }}`,
                },
            ],
            sequence: [
                this.buildWaitAction(node),
                ...this.buildTransitionTail(flow, node.id, edges, fanOutPlans),
            ],
        };
    }
    /**
     * Build a set_variables action from a set_variables node (without state-machine wrapper)
     */
    buildSetVariablesAction(node) {
        // Use spread pattern to preserve unknown properties from custom integrations
        const { alias, id, variables, ...extraProps } = node.data;
        const setVarsAction = {
            ...extraProps, // Preserve extra properties
            variables,
        };
        if (alias) {
            setVarsAction.alias = alias;
        }
        if (id) {
            setVarsAction.id = id;
        }
        return setVarsAction;
    }
    /**
     * Generate block for set_variables node
     */
    generateSetVariablesBlock(flow, node, edges, fanOutPlans) {
        const currentNodeId = node.id;
        return {
            conditions: [
                {
                    condition: 'template',
                    value_template: `{{ current_node == "${currentNodeId}" }}`,
                },
            ],
            sequence: [
                this.buildSetVariablesAction(node),
                ...this.buildTransitionTail(flow, node.id, edges, fanOutPlans),
            ],
        };
    }
    /**
     * Generate passthrough block for unknown node types
     */
    generatePassthroughBlock(flow, node, edges, fanOutPlans) {
        const currentNodeId = node.id;
        return {
            conditions: [
                {
                    condition: 'template',
                    value_template: `{{ current_node == "${currentNodeId}" }}`,
                },
            ],
            sequence: this.buildTransitionTail(flow, node.id, edges, fanOutPlans),
        };
    }
    /**
     * Build Jinja2 template for condition evaluation
     */
    buildConditionTemplate(node) {
        const data = node.data;
        switch (data.condition) {
            case 'state':
                if (data.attribute) {
                    // Use state_attr for attribute checks
                    if (Array.isArray(data.state)) {
                        const states = data.state.map((s) => `'${s}'`).join(', ');
                        return `state_attr('${data.entity_id}', '${data.attribute}') in [${states}]`;
                    }
                    return `state_attr('${data.entity_id}', '${data.attribute}') == '${data.state}'`;
                }
                else {
                    // Use states() for regular state checks
                    if (Array.isArray(data.state)) {
                        const states = data.state.map((s) => `'${s}'`).join(', ');
                        return `states('${data.entity_id}') in [${states}]`;
                    }
                    return `is_state('${data.entity_id}', '${data.state}')`;
                }
            case 'numeric_state':
                return this.buildNumericCondition(data);
            case 'template': {
                // Strip outer {{ }} if present - check both template and value_template
                // Note: Complex templates with {% %} are handled via needsNativeConditionCheck
                // and won't use this method
                let template = data.value_template || 'true';
                if (template.startsWith('{{') && template.endsWith('}}')) {
                    template = template.slice(2, -2).trim();
                }
                return template;
            }
            case 'time':
                return this.buildTimeCondition(data);
            case 'sun':
                return this.buildSunCondition(data);
            case 'zone':
                return `is_state('${data.entity_id}', '${data.zone}')`;
            case 'and':
                if (data.conditions && data.conditions.length > 0) {
                    return `(${data.conditions.map((c) => this.buildNestedCondition(c)).join(' and ')})`;
                }
                return 'true';
            case 'or':
                if (data.conditions && data.conditions.length > 0) {
                    return `(${data.conditions.map((c) => this.buildNestedCondition(c)).join(' or ')})`;
                }
                return 'false';
            case 'not':
                if (data.conditions && data.conditions.length > 0) {
                    return `not (${data.conditions.map((c) => this.buildNestedCondition(c)).join(' and ')})`;
                }
                return 'true';
            default:
                return 'true';
        }
    }
    /**
     * Build numeric state condition template
     */
    buildNumericCondition(data) {
        const parts = [];
        const valueExpr = data.value_template
            ? `(${data.value_template})`
            : data.attribute
                ? `state_attr('${data.entity_id}', '${data.attribute}') | float`
                : `states('${data.entity_id}') | float`;
        if (data.above != null && data.above !== '') {
            parts.push(`${valueExpr} > ${data.above}`);
        }
        if (data.below != null && data.below !== '') {
            parts.push(`${valueExpr} < ${data.below}`);
        }
        return parts.length > 0 ? parts.join(' and ') : 'true';
    }
    /**
     * Build time condition template
     */
    buildTimeCondition(data) {
        const parts = [];
        if (data.after) {
            parts.push(`now().strftime('%H:%M:%S') >= '${data.after}'`);
        }
        if (data.before) {
            parts.push(`now().strftime('%H:%M:%S') < '${data.before}'`);
        }
        if (data.weekday && data.weekday.length > 0) {
            const days = data.weekday.map((d) => `'${d}'`).join(', ');
            parts.push(`now().strftime('%a').lower()[:3] in [${days}]`);
        }
        return parts.length > 0 ? parts.join(' and ') : 'true';
    }
    /**
     * Build sun condition template
     */
    buildSunCondition(data) {
        // Sun conditions check if current time is after sunrise/sunset
        if (data.after === 'sunrise' || data.before === 'sunset') {
            return `is_state('sun.sun', 'above_horizon')`;
        }
        if (data.after === 'sunset' || data.before === 'sunrise') {
            return `is_state('sun.sun', 'below_horizon')`;
        }
        return 'true';
    }
    /**
     * Build nested condition for and/or/not
     */
    buildNestedCondition(condition) {
        // Recursively build the condition template
        const mockNode = {
            id: 'nested',
            type: 'condition',
            position: { x: 0, y: 0 },
            data: condition,
        };
        return this.buildConditionTemplate(mockNode);
    }
    /**
     * Detect if the flow could potentially run forever
     */
    detectPotentialInfiniteLoop(flow, analysis) {
        if (!analysis.hasCycles) {
            return null;
        }
        // Check if all cycles have a condition that could break them
        // This is a simple heuristic - we check if there's at least one condition in the flow
        const hasConditions = flow.nodes.some((n) => n.type === 'condition');
        if (!hasConditions) {
            return ('Warning: This flow contains cycles but no conditions. ' +
                'This could result in an infinite loop. Consider adding a condition to break the cycle.');
        }
        return ('Note: This flow contains cycles. Ensure your conditions can eventually evaluate to ' +
            'break the cycle, or the automation may run indefinitely.');
    }
}
//# sourceMappingURL=state-machine.js.map